/**
 * @file ListStates.tsx
 * @description Состояния списка `ResponsiveList` (phase19.31, шаг О4):
 * заготовки строк при загрузке, ошибка с «Повторить», «пусто» и «пусто под
 * фильтром» со сбросом. Тексты задаёт страница — здесь только раскладка.
 *
 * Заготовки — той же высоты, что строки (таблица 52, список 72/88): пока
 * идёт загрузка, страница не «прыгает».
 */

import { RotateCcw, TriangleAlert } from "lucide-react";

import { Button } from "./button";
import { EmptyState } from "./EmptyState";
import type { ListEmptySpec, ListFilteredEmptySpec } from "./responsive-list-types";
import { Skeleton } from "./skeleton";
import { cn } from "@/lib/utils";

/**
 * Непрозрачная поверхность блока с данными (SC69): токен карточки темы, как
 * у `SectionCard`. Декоративный фон кабинета (SC66) виден только в полях
 * между блоками и не просвечивает сквозь строки.
 */
export const LIST_SURFACE_CLASS = "bg-surface-lowest";

/**
 * Рамка списка — общая у таблицы, компактного списка и заготовок. Настоящая
 * граница, а не внутренняя тень: тень рисуется ПОД содержимым, и подложка
 * шапки таблицы закрывала верхний край рамки (SC69, phase19.31).
 */
export const LIST_FRAME_CLASS = cn(
  "overflow-hidden rounded-xl border border-[var(--border)]",
  LIST_SURFACE_CLASS,
);

/**
 * Подсветки строки на поверхности — НЕПРОЗРАЧНЫЕ (SC69): полупрозрачная
 * подложка (`bg-primary/5`) на карточке поверх фона кабинета пропускала ленты
 * фона. Цвет смешиваем с поверхностью темы, а не с тем, что под блоком.
 */
export const ROW_SURFACE_TINT = {
  /** Только что отсканировали / выбрана — токен темы «выбранная строка». */
  selected: "bg-[var(--row-selected,var(--accent))]",
  /** Недостача, ошибка — красноватая подложка на поверхности. */
  danger: "bg-[color-mix(in_srgb,var(--destructive)_6%,var(--surface-lowest))]",
} as const;

interface SkeletonProps {
  rows: number;
  compact: boolean;
  /** Высокая строка компактного списка (88 вместо 72). */
  tall?: boolean;
}

/** Заготовки строк: шапка таблицы видна сразу, строки — серыми полосами. */
export function ListSkeleton({ rows, compact, tall = false }: SkeletonProps) {
  return (
    <div aria-busy="true" aria-label="Загрузка списка" className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
      {compact ? null : <div className="h-11 bg-[var(--table-head-bg)]" />}
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className={cn("flex flex-col justify-center gap-2 px-3", compact ? (tall ? "min-h-22" : "min-h-18") : "h-13")}
        >
          <Skeleton className="h-4 w-2/5" />
          {compact ? <Skeleton className="h-3 w-3/5" /> : null}
        </div>
      ))}
    </div>
  );
}

interface ErrorProps {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}

/** Ошибка загрузки: причина человеческим текстом и «Повторить». */
export function ListError({ message, onRetry, retryLabel = "Повторить" }: ErrorProps) {
  return (
    <div
      role="alert"
      data-slot="notice-strip"
      data-tone="error"
      className="flex flex-col items-start gap-3 rounded-xl px-4 py-3 sm:flex-row sm:items-center"
    >
      <span className="flex min-w-0 flex-1 items-start gap-2 type-body text-on-surface">
        <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--destructive-text)]" />
        {message}
      </span>
      {onRetry ? (
        <Button variant="outline" onClick={onRetry}>
          <RotateCcw className="size-4" />
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/** «Пусто» или «пусто под фильтром» (со сбросом) — на поверхности, не на фоне. */
export function ListEmpty({ spec, filtered }: { spec: ListEmptySpec | ListFilteredEmptySpec; filtered: boolean }) {
  const reset = filtered && "onReset" in spec && spec.onReset ? spec : null;
  return (
    <EmptyState
      className={LIST_SURFACE_CLASS}
      icon={spec.icon}
      variant={filtered ? "filtered" : "empty"}
      title={spec.title}
      description={spec.description}
      action={
        reset ? (
          <Button variant="outline" onClick={reset.onReset}>
            {reset.resetLabel ?? "Сбросить фильтры"}
          </Button>
        ) : (
          spec.action
        )
      }
    />
  );
}
